import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SharedModule } from '@aiwyn/shared';
import { SafeDatePipe } from '@aiwyn/shared/pipes/safe-date/safe-date.pipe';
import { CrlItemStatus, CrlRequestItem, isCrlItemBulkSelectable } from '../../../../models';
import { buildCrlItemAddons, getCrlStatusIcon, getCrlStatusTooltip } from '../../../../shared';

/**
 * A single request-item row in the CRL list view (Figma "REQUEST ITEM",
 * node `11154:78134`). Four slots, left to right:
 *
 *  - the leading check circle (empty / green check / red alert, derived from the
 *    item status — see {@link getCrlStatusIcon}), replaced by a lock when the
 *    item is restricted and this caller has no access,
 *  - the title row: `A.1` prefix, item name, and a lock when the item is
 *    restricted and this caller does have access,
 *  - the description,
 *  - the metadata slot (the due/finished date) and, for a restricted item this
 *    caller cannot see into, the restricted notice.
 *
 * The name and the description are each ellipsised to a single line, so rows
 * stay a uniform height however long their text is; `tooltipOverflow` gives the
 * name a hover tooltip with its full text, but only while it is actually cut off.
 *
 * A right-hand add-on slot carries the comment indicator (with a teal dot while
 * something on the item is unread), the marked-as-N/A affordance, the file count
 * and the assignee avatar stack, ordered by {@link buildCrlItemAddons}; on the
 * narrow mobile column it wraps under the metadata and the description hides.
 *
 * Clicking (or keyboard-activating) the row emits `itemSelected`; the container
 * navigates to the child `request/:requestId` panel route. N/A items render
 * muted with the title, description and date struck through.
 */
@Component({
  selector: 'cxp-crl-item-row',
  templateUrl: './crl-item-row.component.html',
  styleUrls: ['./crl-item-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule, SafeDatePipe, MatCheckboxModule],
})
export class CrlItemRowComponent {
  readonly item = input.required<CrlRequestItem>();
  /** Section identifier + item ordinal, e.g. `A.1` — composed by the section list. */
  readonly displayNumber = input<string>('');
  /** True while bulk-actions selection mode is active (renders the checkbox, REQ-11). */
  readonly selectionMode = input<boolean>(false);
  /** True when this row is currently checked. */
  readonly selected = input<boolean>(false);
  /** True while this item's undo is in flight — the row stays N/A until the server answers, so the link has to close itself. */
  readonly undoPending = input<boolean>(false);
  readonly itemSelected = output<CrlRequestItem>();
  /** Emitted when the row's selection checkbox is toggled (selection mode only). */
  readonly selectionToggled = output<CrlRequestItem>();
  /** Emitted by the add-on slot's Undo link — returns an N/A item to its open state. */
  readonly notApplicableUndone = output<CrlRequestItem>();
  /** The comment control was activated — open this item straight on its thread. */
  readonly commentsSelected = output<CrlRequestItem>();

  /** Whether this row may be selected in bulk mode (restricted rows are not). */
  protected readonly isSelectable = computed(() => isCrlItemBulkSelectable(this.item()));

  /** Leading status circle (empty / green check / red exclamation) — drives the icon name + tone class. */
  protected readonly statusIcon = computed(() => getCrlStatusIcon(this.item().status));
  protected readonly isOverdue = computed(() => this.item().status === CrlItemStatus.OVERDUE);
  /** Whole days an overdue item is past its due date (≥ 1) — feeds the overdue tooltip copy. */
  protected readonly daysOverdue = computed(() => {
    if (!this.isOverdue()) return 0;
    const dueDate = this.item().dueDate;
    if (!dueDate) return 0;
    const elapsedMs = Date.now() - new Date(dueDate).getTime();
    return Math.max(1, Math.ceil(elapsedMs / 86_400_000));
  });
  /** Hover tooltip for the status circle (Figma node 11158:78211). */
  protected readonly statusTooltip = computed(() => getCrlStatusTooltip(this.item().status, this.daysOverdue()));
  protected readonly isNotApplicable = computed(() => this.item().status === CrlItemStatus.NOT_APPLICABLE);

  /**
   * The attention states (Figma "Overdue/Needs Update/Attention") — the row
   * takes a warm tint and the check circle turns into a red `!`.
   */
  protected readonly needsAttention = computed(() => {
    const status = this.item().status;
    return status === CrlItemStatus.OVERDUE || status === CrlItemStatus.NEEDS_UPDATE;
  });

  /** True when the item is access-restricted to specific recipients. */
  protected readonly isRestricted = computed(() => !!this.item().isRestricted);
  /**
   * A restricted item whose content this caller cannot see: `/page` masks the
   * content (`isContentMasked` → `isAnswerMasked`) for exactly those callers.
   * The lock takes the check circle's place, the row greys out and the
   * restricted notice renders.
   */
  protected readonly isRestrictedWithoutAccess = computed(() => this.isRestricted() && !!this.item().isAnswerMasked);
  /** A restricted item this caller can see into — an inline lock sits after the name. */
  protected readonly isRestrictedWithAccess = computed(() => this.isRestricted() && !this.item().isAnswerMasked);

  /**
   * The metadata slot's date line (Figma node `11158:79183`).
   *
   * The date is always the item's DUE date — the `/page` read carries no
   * per-item completion date — so the finished variants label the due date
   * "Finished", matching the frame, which draws both variants on the same date.
   */
  protected readonly metadata = computed(() => {
    const item = this.item();
    if (!item.dueDate) return null;

    switch (item.status) {
      case CrlItemStatus.OVERDUE:
        return { label: 'Overdue', date: item.dueDate, isOverdue: true, isStruckThrough: false };
      case CrlItemStatus.FINISHED:
        return { label: 'Finished', date: item.dueDate, isOverdue: false, isStruckThrough: false };
      case CrlItemStatus.NOT_APPLICABLE:
        return { label: 'Finished', date: item.dueDate, isOverdue: false, isStruckThrough: true };
      default:
        return { label: 'Due', date: item.dueDate, isOverdue: false, isStruckThrough: false };
    }
  });

  /** The right-hand add-on slot's entries, already in the design's left-to-right order. */
  protected readonly addons = computed(() => buildCrlItemAddons(this.item()));

  onActivate(): void {
    // In selection mode, activating the row toggles its checkbox (when
    // selectable) instead of opening the detail panel.
    if (this.selectionMode()) {
      if (this.isSelectable()) this.selectionToggled.emit(this.item());
      return;
    }
    this.itemSelected.emit(this.item());
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onActivate();
    }
  }

  onSelectionToggled(): void {
    if (this.isSelectable()) this.selectionToggled.emit(this.item());
  }

  onUndoNotApplicable(event: Event): void {
    // The Undo link sits inside the row's own click target.
    event.stopPropagation();
    this.notApplicableUndone.emit(this.item());
  }

  /**
   * The comment control opens the item on its Comments tab. The row underneath
   * is itself clickable, so the event stops here — otherwise the row's own
   * handler would follow and reopen on the default tab.
   */
  protected onCommentsActivated(event: Event): void {
    event.stopPropagation();
    this.commentsSelected.emit(this.item());
  }
}
